import { isDefined } from "@draftkings/sportsbook-common-utils";
import { action, computed, observable, makeObservable, toJS } from "mobx";
import { getFilterId, areArraysEqual, getSelectedFilterName, SortedMap, } from "../../../businessLayer/Shared";
import { parseFilterName, ArrayIndex, parsePrePack, parseEvent, pointsFormatBuilder, getEventName, doesAllSelectionsHaveSamePrePackId, getAnalyticsOutcomeAddedRemovedResults, compareEvents, tryFindTeamLogo, buildTeamNameIndexKey, getPrePackAvailability, getEventTags, isEventShouldShowScoreboards, sortParticipants } from "../helpers";
import { parseScoreboards } from "@draftkings/scoreboards";
export class PrePacksParser {
    constructor(options) {
        this.mode = options.mode;
        this.dict = options.dict;
        this.localizationLib = options.localizationLib;
        this.consumerCommands = options.consumerCommands;
        this.prePacksRetriever = options.prePacksRetriever;
        this.scoreboardsRetriever = options.scoreboardsRetriever;
        this.isSwooshTeamLogosEnabledForPrePacks = options.isSwooshTeamLogosEnabledForPrePacks;
        this.swooshBaseUrl = options.swooshBaseUrl;
        this.buildScoreboardInternaly = options.buildScoreboardInternaly;
        this.logosMap = options.logosMap;
        this.staticS3Host = options.staticS3Host;
        this.oddsStyle = options.oddsStyle;
        this.selectedFilterId = "";
        this.togglePrePacksStatusesRetriever = options.togglePrePacksStatusesRetriever;
        this.filteredPrePacksByEventId = new ArrayIndex(eventId => this.prePacks
            .filter(x => x.selections[0].eventId === eventId), prePacks => prePacks
            .map(x => parsePrePack({
            prePack: x,
            oddsStyle: this.oddsStyle,
            localization: { formatPoints: pointsFormatBuilder(this.localizationLib) }
        })));
        this.togglePrePackData = undefined;
        this.isMobile = options.isMobile;
        this.onMobileToggleSelection = options.onMobileToggleSelection;
        makeObservable(this, {
            oddsStyle: observable,
            selectedFilterId: observable,
            togglePrePackData: observable,
            processSelections: action,
            selectFilter: action,
            togglePrePack: action,
            prePacksStatusError: computed,
            isPrePackProcessing: computed,
            teamNames: computed,
            prePacksFilters: computed,
            filteredEvents: computed,
            selectedOrDefaultFilterId: computed,
            scoreboardData: computed,
            clearData: action,
            setOddsStyle: action,
        });
    }
    clearData() {
        this.selectedFilterId = "";
        this.togglePrePackData = undefined;
    }
    activate() {
        this.prePacksRetriever.activate();
        this.togglePrePacksStatusesRetriever.activate();
        if (this.buildScoreboardInternaly && this.mode.type === "League") {
            this.scoreboardsRetriever.loadEventsData(this.mode.id);
        }
    }
    deactivate() {
        this.prePacksRetriever.deactivate();
        this.togglePrePacksStatusesRetriever.deactivate();
        this.clearData();
    }
    setParams(params) {
        var _a;
        this.prePacksRetriever.setParams(params);
        (_a = params.restorationOptions) === null || _a === void 0 ? void 0 : _a.forEach(x => {
            this.processSelections(x);
        });
    }
    setOddsStyle(oddsStyle) {
        this.oddsStyle = oddsStyle;
    }
    // TODO: think about refactoring to make less conditions here. - https://jira.dkcorpit.com/browse/SB-24740
    /**
     * Check @see ITogglePrePacksStatusRetriever comment for explanation of possible cases
     * @param evenId event id
     * @param prePackId prePack id
     * @returns modifier - {@link PrePackToggleModifier}
     */
    getPrePackModifier(evenId, prePackId) {
        const targetPrePack = this.filteredPrePacksByEventId.getData(evenId)
            .find(x => x.id === prePackId);
        // As this method is called from State over existing prePacks - it's not real case
        if (!targetPrePack) {
            return "default";
        }
        const availabilityStatus = getPrePackAvailability(targetPrePack);
        if (availabilityStatus) {
            return availabilityStatus;
        }
        const status = this.togglePrePacksStatusesRetriever.get(evenId, prePackId);
        if (!status) {
            return "default";
        }
        if (status.type === "Loading") {
            return "loading";
        }
        return areArraysEqual(status.selectionIds, targetPrePack.selections.map(x => x.id))
            ? "selected"
            : "default";
    }
    processSelections(data) {
        if (doesAllSelectionsHaveSamePrePackId(data.selections)) {
            this.trySelectPrePack(data.eventId, data.selections);
        }
        else {
            this.deselectPrePack(data);
        }
        this.togglePrePackData = undefined;
    }
    trySelectPrePack(eventId, selections) {
        const prePackId = selections[0].prePackId;
        if (this.shouldProcessOutcomeResults(prePackId, "IsAdding")) {
            const prePack = this.filteredPrePacksByEventId
                .getData(eventId)
                .find(x => x.id === prePackId);
            getAnalyticsOutcomeAddedRemovedResults({
                eventId,
                prePack,
                modifier: this.getPrePackModifier(eventId, prePackId),
                filterName: getSelectedFilterName(this.prePacksFilters, f => f.id === this.selectedOrDefaultFilterId),
                betslipSelections: selections,
            }).forEach(x => this.consumerCommands.sendAnalytics(x));
        }
        this.togglePrePacksStatusesRetriever.set({
            eventId,
            prePackId,
            status: {
                type: "Selected",
                selectionIds: selections.map(x => x.id),
            },
        });
    }
    deselectPrePack(data) {
        const prePacks = this.filteredPrePacksByEventId.getData(data.eventId);
        prePacks.forEach(prePack => {
            if (this.shouldProcessOutcomeResults(prePack.id, "IsRemoving")) {
                getAnalyticsOutcomeAddedRemovedResults({
                    eventId: data.eventId,
                    prePack,
                    modifier: this.getPrePackModifier(data.eventId, prePack.id),
                    filterName: getSelectedFilterName(this.prePacksFilters, f => f.id === this.selectedOrDefaultFilterId),
                    betslipSelections: [],
                }).forEach(x => this.consumerCommands.sendAnalytics(x));
            }
        });
        this.togglePrePacksStatusesRetriever.delete(data.eventId);
    }
    shouldProcessOutcomeResults(prePackId, action) {
        return this.togglePrePackData !== undefined
            && this.togglePrePackData.action === action
            && this.togglePrePackData.prePackId === prePackId;
    }
    get prePacksStatusError() {
        var _a;
        return ((_a = this.prePacksRetriever.error) === null || _a === void 0 ? void 0 : _a.entityId) ? this.prePacksRetriever.error : undefined;
    }
    get isPrePackProcessing() {
        return this.togglePrePackData !== undefined;
    }
    get teamNames() {
        return [...this.prePacksRetriever.events.values()].reduce((acc, e) => {
            e.participants.forEach(p => {
                if (!p.venueRole) {
                    return;
                }
                acc.set(buildTeamNameIndexKey(e.id, p.venueRole), p.name);
            });
            return acc;
        }, new Map());
    }
    get prePacksFilters() {
        const filters = [...this.prePacksRetriever.filters.values()];
        return filters
            .filter(f => f.count + f.inGameCount)
            .sort((x, y) => x.sortOrder - y.sortOrder).map(x => ({
            id: getFilterId(x),
            name: parseFilterName({
                dict: this.dict,
                filter: x,
                events: this.prePacksRetriever.events,
                oddsStyle: this.oddsStyle,
            }),
            logo: tryFindTeamLogo({
                teamNames: this.teamNames,
                filter: x,
                staticS3Host: this.mode.params.staticS3Host,
                teamsLogos: this.mode.params.teamsLogos,
            })
        }));
    }
    getEventScoreboardParsedData(eventId) {
        const event = this.scoreboardsRetriever.getPrePacksEventScoreboardData(eventId);
        if (!event) {
            return undefined;
        }
        const participants = sortParticipants(event);
        const res = parseScoreboards({
            event: Object.assign(Object.assign({}, event), { participants }),
            logosMap: this.logosMap,
            staticS3Host: this.staticS3Host,
            isTeamSwap: false,
            isSwooshTeamLogosEnabled: this.isSwooshTeamLogosEnabledForPrePacks,
            swooshBaseUrl: this.swooshBaseUrl,
        });
        return res;
    }
    get filteredEvents() {
        let logos = {};
        let staticS3Host = "";
        const prePacksEventIds = new Set(this.prePacks
            .map(x => x.selections[0].eventId));
        if (this.mode.type === "League") {
            logos = this.mode.params.teamsLogos;
            staticS3Host = this.mode.params.staticS3Host;
        }
        return [...prePacksEventIds.values()]
            .map(id => this.prePacksRetriever.events.get(id))
            .filter(isDefined)
            .sort(compareEvents)
            .map(event => parseEvent({
            event,
            logos,
            staticS3Host,
            hasScoreboard: this.hasScoreboard(event.id),
            isSwooshTeamLogosEnabledForPrePacks: this.isSwooshTeamLogosEnabledForPrePacks,
            swooshBaseUrl: this.swooshBaseUrl,
            formatEventDate: this.mode.type === "League"
                ? this.mode.params.formatEventDate
                : undefined,
            dict: this.dict,
        }));
    }
    get selectedOrDefaultFilterId() {
        return this.selectedFilterId === ""
            ? getFilterId([...this.prePacksRetriever.filters.values()].find(x => x.isDefault))
            : this.selectedFilterId;
    }
    get scoreboardData() {
        return this.scoreboardsRetriever.scoreboardData;
    }
    togglePrePack(eventId, prePackId) {
        if (this.isMobile()) {
            this.onMobileToggleSelection();
            return;
        }
        this.togglePrePackData = {
            prePackId,
            action: this.getPrePackModifier(eventId, prePackId) === "default"
                ? "IsAdding"
                : "IsRemoving"
        };
        this.togglePrePacksStatusesRetriever.set({
            eventId,
            prePackId,
            status: {
                type: "Loading",
            },
        });
    }
    getSelectionsState(eventId, prePackId) {
        const modifier = this.getPrePackModifier(eventId, prePackId);
        const prePack = this.getPrePacksForCurrentFilter().get(prePackId);
        const defaultState = {
            selections: [],
            sportEventId: "",
            sender: "PrePacks",
            isLive: false,
        };
        if (!prePack) {
            return defaultState;
        }
        if (modifier === "loading") {
            return Object.assign(Object.assign({}, defaultState), { isLive: prePack.isLive });
        }
        if (modifier === "selected") {
            return Object.assign(Object.assign({}, defaultState), { sportEventId: eventId, isLive: prePack.isLive });
        }
        const event = this.prePacksRetriever.events.get(eventId);
        return {
            selections: prePack.selections.map(s => ({
                selectionId: s.id,
                eventId: s.eventId,
                eventName: getEventName(event),
                marketId: s.marketId,
                marketName: s.marketName,
                outcomeType: s.outcomeType,
                points: s.points,
                prePackId,
                selectionName: s.selectionName,
                tags: toJS(getEventTags(event))
            })),
            sportEventId: eventId,
            sender: "PrePacks",
            isLive: prePack.isLive,
        };
    }
    selectFilter(id) {
        if (this.selectedFilterId === id) {
            return;
        }
        this.selectedFilterId = id;
        this.prePacksRetriever.changeFilter(id);
    }
    sendOutcomeActionAnalytics(eventId, metadata) {
        this.consumerCommands.sendAnalytics({
            type: "OutcomeAction",
            sportEventId: eventId,
            metadata,
        });
    }
    getPrePacksForCurrentFilter() {
        return this.prePacksRetriever.prePacks.get(this.selectedOrDefaultFilterId)
            || new SortedMap();
    }
    get prePacks() {
        return this.getPrePacksForCurrentFilter().sortedValues();
    }
    hasScoreboard(eventId) {
        if (this.buildScoreboardInternaly) {
            const event = this.scoreboardsRetriever.getPrePacksEventScoreboardData(eventId);
            return isEventShouldShowScoreboards(event);
        }
        else {
            return this.scoreboardData.has(eventId);
        }
    }
}
//# sourceMappingURL=PrePacksParser.js.map